import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { endpoints } from '../api/client.js';
import { useResource } from '../hooks/useResource.js';
import PageHeader from '../components/PageHeader.jsx';
import { Panel } from '../components/Panel.jsx';
import { StatTile, TileRow } from '../components/StatTile.jsx';
import { Meter } from '../components/Meter.jsx';
import { Resource, StatusGlyph } from '../components/StateViews.jsx';
import {
  formatClock,
  formatGb,
  formatLoad,
  formatMb,
  formatNumber,
  formatUptime,
  percent,
} from '../lib/format.js';
import { usageTone } from '../lib/status.js';
import SitePreview from '../components/SitePreview.jsx';
import { Icon } from '../components/Brand.jsx';
import { usePins } from '../pins/PinsContext.jsx';
import { liveDomain } from '../lib/slots.js';

const REFRESH_MS = 15000;

export default function Dashboard() {
  const [paused, setPaused] = useState(false);
  const pins = usePins();

  const load = useCallback((signal) => endpoints.health(signal), []);
  const state = useResource(load, [], { intervalMs: REFRESH_MS, paused });
  const loadSites = useCallback((signal) => endpoints.sites(signal), []);
  const sitesState = useResource(loadSites, []);

  const host = state.data?.hostname;

  return (
    <div className="page">
      <PageHeader
        eyebrow="Presto Studio"
        title="Your studio"
        lede={
          host ? (
            <>
              Your live sites and the host behind them, all in one workspace.
            </>
          ) : (
            'Live health for the hosting node.'
          )
        }
        meta={
          <p className="refresh-meta">
            {state.updatedAt ? (
              <>
                Updated <span className="mono">{formatClock(state.updatedAt)}</span>
              </>
            ) : (
              'Not yet updated'
            )}
            <span className="refresh-meta__sep">·</span>
            {paused ? 'auto-refresh paused' : `every ${REFRESH_MS / 1000}s`}
          </p>
        }
        actions={
          <>
            <button
              type="button"
              className="btn btn--quiet"
              onClick={() => setPaused((value) => !value)}
              aria-pressed={paused}
            >
              {paused ? 'Resume' : 'Pause'}
            </button>
            <button type="button" className="btn" onClick={() => { state.refresh(); sitesState.refresh(); }}>
              Refresh
            </button>
          </>
        }
      />

      {state.staleError ? (
        <p className="notice notice--warn" role="status">
          <StatusGlyph tone="warning" shape="ring" />
          Last refresh failed ({state.staleError.message}). Showing the previous reading.
        </p>
      ) : null}

      <Resource
        state={sitesState}
        loadingLabel="Loading your sites…"
        loadingRows={3}
        errorTitle="Could not load site previews"
        isEmpty={(data) => !Array.isArray(data) || data.length === 0}
        emptyTitle="No sites yet"
        emptyBody="Sites will appear here as soon as they are added to the studio."
      >
        {(sites) => <WorkspaceSites sites={sites} pinNames={pins.sites} pinStatus={pins.status} />}
      </Resource>

      <Resource
        state={state}
        loadingLabel="Reading host health…"
        loadingRows={4}
        errorTitle="Could not read host health"
        emptyTitle="No health data"
        emptyBody="The host returned an empty health report."
      >
        {(health) => <HealthBoard health={health} />}
      </Resource>
    </div>
  );
}

function WorkspaceSites({ sites, pinNames, pinStatus }) {
  const pinnedSites = pinNames
    .map((name) => sites.find((site) => site.name === name))
    .filter(Boolean);

  return (
    <section className="workspace-sites" aria-labelledby="workspace-sites-title">
      <div className="workspace-sites__head">
        <div><p className="cap">Pinned projects</p><h2 id="workspace-sites-title">Live site previews</h2></div>
        <Link className="btn btn--quiet" to="/sites">View all sites</Link>
      </div>
      {pinStatus === 'loading' ? (
        <p className="workspace-sites__loading">Loading pinned sites…</p>
      ) : pinnedSites.length === 0 ? (
        <div className="workspace-sites__empty">
          <span className="workspace-sites__empty-icon"><Icon name="pin" size={19} /></span>
          <span><strong>No pinned sites yet</strong><small>Pin up to five sites for quick access and live previews.</small></span>
          <Link className="btn" to="/sites">Choose sites</Link>
        </div>
      ) : <div className="workspace-sites__grid">
        {pinnedSites.slice(0, 3).map((site) => (
          <article className="workspace-site" key={site.name}>
            <div className="workspace-site__meta">
              <span><strong>{site.name}</strong><small>{liveDomain(site) || 'No live domain connected'}</small></span>
              <Link to={`/sites/${encodeURIComponent(site.name)}`}>Manage</Link>
            </div>
            <SitePreview site={site} compact />
          </article>
        ))}
      </div>}
    </section>
  );
}

function HealthBoard({ health }) {
  const memory = health.memory || {};
  const disk = health.disk || {};
  const loadAvg = Array.isArray(health.load) ? health.load : [];
  const cores = health.cpuCount || null;

  const memPct = percent(memory.usedMb, memory.totalMb);
  const diskPct =
    typeof disk.usedPercent === 'number' ? disk.usedPercent : percent(disk.usedGb, disk.totalGb);

  const loadPct = cores && loadAvg.length ? Math.min(100, (loadAvg[0] / cores) * 100) : null;

  const sitesRunning = health.runningCount ?? 0;
  const sitesTotal = health.siteCount ?? 0;
  const allRunning = sitesTotal > 0 && sitesRunning === sitesTotal;

  return (
    <>
      <TileRow>
        <StatTile
          label="Sites"
          value={formatNumber(sitesTotal)}
          detail={sitesTotal === 1 ? 'registered site' : 'registered sites'}
        />
        <StatTile
          label="Running sites"
          value={formatNumber(sitesRunning)}
          detail={
            sitesTotal > 0 ? (
              <>
                of {formatNumber(sitesTotal)} · {allRunning ? 'all up' : `${sitesTotal - sitesRunning} down`}
              </>
            ) : (
              'no sites registered'
            )
          }
        />
        <StatTile
          label="Load average"
          value={formatLoad(loadAvg[0])}
          detail={
            <span className="mono">
              {formatLoad(loadAvg[1])} · {formatLoad(loadAvg[2])}
            </span>
          }
          note={cores ? `1m · 5m · 15m across ${cores} core${cores === 1 ? '' : 's'}` : '1m · 5m · 15m'}
        >
          {loadPct !== null ? (
            <Meter
              used={loadAvg[0]}
              total={cores}
              readout={`${formatLoad(loadAvg[0])} / ${cores} cores`}
              showPercent={false}
            />
          ) : null}
        </StatTile>
        <StatTile
          label="Uptime"
          value={formatUptime(health.uptimeSeconds)}
          detail={<span className="mono">{formatNumber(health.uptimeSeconds)} s</span>}
          note="Since last host boot"
        />
      </TileRow>

      <div className="grid grid--2">
        <Panel title="Memory" subtitle="Resident usage across the host" id="memory">
          <StatTile
            label="Used"
            value={formatMb(memory.usedMb)}
            detail={`${formatMb(memory.freeMb)} free of ${formatMb(memory.totalMb)}`}
          >
            <Meter used={memory.usedMb} total={memory.totalMb} format={formatMb} />
          </StatTile>
          <p className="panel__note">
            {toneSentence('Memory', memPct)}
          </p>
        </Panel>

        <Panel title="Disk" subtitle="Root filesystem" id="disk">
          <StatTile
            label="Used"
            value={formatGb(disk.usedGb)}
            detail={`${formatGb(disk.freeGb)} free of ${formatGb(disk.totalGb)}`}
          >
            <Meter used={disk.usedGb} total={disk.totalGb} format={formatGb} />
          </StatTile>
          <p className="panel__note">{toneSentence('Disk', diskPct)}</p>
        </Panel>
      </div>

      <Panel
        title="Services"
        subtitle="systemd units the platform depends on"
        id="services"
      >
        <ServiceList services={health.services} />
      </Panel>
    </>
  );
}

function toneSentence(subject, pct) {
  const tone = usageTone(pct);
  if (tone === 'critical') return `${subject} is critically high. Under 10% headroom remains.`;
  if (tone === 'warning') return `${subject} is high. Under 25% headroom remains.`;
  if (tone === 'nominal') return `${subject} is within the normal range.`;
  return `${subject} usage is not reported.`;
}

function ServiceList({ services }) {
  if (!Array.isArray(services) || services.length === 0) {
    return (
      <p className="state__body">
        No services were reported by the host. HAProxy is the only public listener, so an empty
        list here is worth investigating.
      </p>
    );
  }

  return (
    <ul className="servicelist">
      {services.map((service) => {
        const tone = service.active ? 'good' : 'critical';
        return (
          <li className="servicelist__item" key={service.name}>
            <span className="servicelist__name mono">{service.name}</span>
            <span className={`pill pill--${tone}`}>
              <StatusGlyph tone={tone} shape={service.active ? 'disc' : 'diamond'} />
              {service.active ? 'Active' : 'Not running'}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
